/**
 * Noticing that the Mac is waiting for a password.
 *
 * There is no signal for this on the wire and there cannot be: `sudo` asks by printing
 * a line and turning off echo, both of which happen inside the PTY. So we read the same
 * thing a person reads — the last line on screen — and offer the masked field when it
 * looks like a prompt.
 *
 * Deliberately a suggestion, not a mode: a false positive shows a field nobody has to
 * use, and a false negative is covered by the key button that opens the same field by
 * hand. Nothing here changes what is sent, only what the phone offers to type into.
 */

/** Enough to hold the longest prompt we care about and nothing more. */
const TAIL_MAX = 240;

/**
 * CSI, OSC and the two-byte escapes, which is everything a prompt line can be wrapped
 * in. `sudo` itself prints plain text, but the shell around it repaints the line with
 * colour, and an unstripped `\x1b[0m` would sit between the colon and the end of the
 * string and defeat the anchor below.
 */
const ANSI = /\x1b\][^\x07\x1b]*(?:\x07|\x1b\\)|\x1b\[[0-9;?<>=]*[ -/]*[@-~]|\x1b[@-Z\\-_]/g;

/**
 * Prompts worth catching, all of them ending in a colon the cursor sits after:
 * `Password:` from sudo, `Password for 'https://…':` from git, `Enter passphrase for
 * key …:` from ssh, `[sudo] password for kirill:` from a Linux box over ssh.
 *
 * Anchored to the trailing colon, which is what keeps `grep password notes.txt` and a
 * directory listing containing `password.txt` from matching: a real prompt ends with the
 * colon and a waiting cursor, and ordinary output carrying the word does not.
 *
 * Two details cost a test each. The middle allows colons, because git's prompt has a URL
 * in it (`Password for 'https://github.com':`). And the word boundary is a Unicode
 * lookahead rather than `\b`, because `\b` is ASCII-only in JavaScript and therefore sees
 * no boundary at all between `Пароль` and the colon after it.
 */
const PROMPT = /(?:^|[\s[(])(?:\[sudo\]\s*)?(?:password|passphrase|пароль)(?![\p{L}\p{N}_])[^\n]{0,80}:\s*$/iu;

/** The last line of what the terminal has printed, with escapes and returns resolved. */
export function appendTail(prev: string, chunk: string): string {
  const joined = (prev + chunk).replace(ANSI, '');
  // A carriage return means the line was rewritten from its start, so anything before
  // the last one is no longer on screen and must not be matched against.
  const lastLine = joined.split('\n').pop() ?? '';
  const rewritten = lastLine.split('\r').pop() ?? '';
  return rewritten.slice(-TAIL_MAX);
}

export function isPasswordPrompt(tail: string): boolean {
  return PROMPT.test(tail);
}
